import { useCallback, useMemo, useState } from 'react';
import type { FilterOptionsResponse } from '@/api/types';
import { useT } from '@/i18n/LocaleProvider';
import type { UiKey } from '@/i18n/dictionary';
import {
  FEATURES,
  FILTER_GROUP_ORDER,
  LEVEL_VALUES,
  ORIENTATION_VALUES,
  PACING_VALUES,
  TUTORIAL_PLATFORMS,
  parentSelection,
  toggleChildCategory,
  toggleParentCategory,
  toggleValue,
  type CatalogFilters,
  type FilterGroupKey,
} from './filterModel';

export interface FilterItem {
  key: string;
  name: string;
  on: boolean;
  child?: boolean;
  /** «2 of 3» у родителя с частично выбранными детьми. */
  sub?: string;
  hasKids?: boolean;
  expanded?: boolean;
  onToggle: () => void;
  onExpand?: () => void;
}

export interface FilterGroup {
  key: FilterGroupKey;
  label: string;
  count: number;
  items: FilterItem[];
}

export interface ActiveChip {
  key: string;
  label: string;
  onRemove: () => void;
}

type SetGroup = (
  key: FilterGroupKey,
  values: CatalogFilters[FilterGroupKey],
  action: 'add' | 'remove' | 'set',
  value?: string | number,
) => void;

/** Группы чипов и активные значения из фильтров и справочников (CAT-2, CAT-3, CAT-5). */
export function useFilterGroups(
  filters: CatalogFilters,
  options: FilterOptionsResponse['data'] | undefined,
  setGroup: SetGroup,
) {
  const t = useT();
  // Раскрытые категории помнятся до выхода (CAT-6); по умолчанию раскрыты те, где выбран ребёнок.
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const toggleExpanded = useCallback(
    (id: number, current: boolean) => setExpanded((prev) => ({ ...prev, [id]: !current })),
    [],
  );

  const groups = useMemo<FilterGroup[]>(() => {
    const labels: Record<FilterGroupKey, string> = {
      category_ids: t('filters.category'),
      transition_type_ids: t('filters.effects'),
      pacing: t('filters.pacing'),
      production_level: t('filters.level'),
      features: t('filters.features'),
      hook_ids: t('filters.hooks'),
      orientation: t('filters.orientation'),
      tutorial_platforms: t('filters.tutorials'),
    };

    const simple = <T extends string | number>(
      key: FilterGroupKey,
      values: readonly T[],
      name: (v: T) => string,
    ): FilterItem[] =>
      values.map((value) => {
        const list = filters[key] as unknown as readonly T[];
        const on = list.includes(value);
        return {
          key: String(value),
          name: name(value),
          on,
          onToggle: () =>
            setGroup(
              key,
              toggleValue(list, value) as CatalogFilters[FilterGroupKey],
              on ? 'remove' : 'add',
              name(value),
            ),
        };
      });

    const categoryItems: FilterItem[] = [];
    for (const parent of options?.categories ?? []) {
      const { on, kidsOn } = parentSelection(filters.category_ids, parent);
      const isExpanded = expanded[parent.id] ?? kidsOn > 0;
      categoryItems.push({
        key: String(parent.id),
        name: parent.name,
        on,
        sub:
          kidsOn && !on
            ? t('catalog.partOf', { n: kidsOn, total: parent.children.length })
            : undefined,
        hasKids: parent.children.length > 0,
        expanded: isExpanded,
        onToggle: () =>
          setGroup(
            'category_ids',
            toggleParentCategory(filters.category_ids, parent),
            on ? 'remove' : 'add',
            parent.name,
          ),
        onExpand: () => toggleExpanded(parent.id, isExpanded),
      });
      if (isExpanded) {
        for (const child of parent.children) {
          const childOn = filters.category_ids.includes(child.id);
          categoryItems.push({
            key: String(child.id),
            name: child.name,
            on: childOn,
            child: true,
            onToggle: () =>
              setGroup(
                'category_ids',
                toggleChildCategory(filters.category_ids, parent, child.id),
                childOn ? 'remove' : 'add',
                child.name,
              ),
          });
        }
      }
    }

    const byKey: Record<FilterGroupKey, FilterItem[]> = {
      category_ids: categoryItems,
      transition_type_ids: simple(
        'transition_type_ids',
        (options?.transition_types ?? []).map((o) => o.id),
        (id) => options?.transition_types.find((o) => o.id === id)?.name ?? String(id),
      ),
      pacing: simple('pacing', PACING_VALUES, (v) => t(`values.pacing.${v}` as UiKey)),
      production_level: simple('production_level', LEVEL_VALUES, (v) =>
        t(`values.level.${v}` as UiKey),
      ),
      features: simple(
        'features',
        FEATURES.map((f) => f.key),
        (k) => t(`values.features.${k}` as UiKey),
      ),
      hook_ids: simple(
        'hook_ids',
        (options?.hooks ?? []).map((o) => o.id),
        (id) => options?.hooks.find((o) => o.id === id)?.name ?? String(id),
      ),
      orientation: simple('orientation', ORIENTATION_VALUES, (v) =>
        t(`values.orientation.${v}` as UiKey),
      ),
      tutorial_platforms: simple(
        'tutorial_platforms',
        TUTORIAL_PLATFORMS.map((p) => p.code),
        (c) => TUTORIAL_PLATFORMS.find((p) => p.code === c)?.label ?? c,
      ),
    };

    return FILTER_GROUP_ORDER.map((key) => ({
      key,
      label: labels[key],
      count: filters[key].length,
      items: byKey[key],
    }));
  }, [filters, options, expanded, setGroup, t, toggleExpanded]);

  const active = useMemo<ActiveChip[]>(() => {
    const chips: ActiveChip[] = [];
    const nameOf = (group: FilterGroupKey, value: string | number): string => {
      switch (group) {
        case 'category_ids': {
          for (const parent of options?.categories ?? []) {
            if (parent.id === value) return parent.name;
            const child = parent.children.find((c) => c.id === value);
            if (child) return child.name;
          }
          return String(value);
        }
        case 'transition_type_ids':
          return options?.transition_types.find((o) => o.id === value)?.name ?? String(value);
        case 'hook_ids':
          return options?.hooks.find((o) => o.id === value)?.name ?? String(value);
        case 'features':
          return t(`values.features.${String(value)}` as UiKey);
        case 'pacing':
          return t(`values.pacing.${String(value)}` as UiKey);
        case 'production_level':
          return t(`values.level.${String(value)}` as UiKey);
        case 'orientation':
          return t(`values.orientation.${String(value)}` as UiKey);
        case 'tutorial_platforms':
          return TUTORIAL_PLATFORMS.find((p) => p.code === value)?.label ?? String(value);
        default:
          return String(value);
      }
    };
    for (const key of FILTER_GROUP_ORDER) {
      for (const value of filters[key] as readonly (string | number)[]) {
        chips.push({
          key: `${key}:${value}`,
          label: nameOf(key, value),
          onRemove: () =>
            setGroup(
              key,
              (filters[key] as readonly (string | number)[]).filter(
                (v) => v !== value,
              ) as CatalogFilters[FilterGroupKey],
              'remove',
              nameOf(key, value),
            ),
        });
      }
    }
    return chips;
  }, [filters, options, setGroup, t]);

  return { groups, active };
}
